import { ColorRepresentation, Mesh, MeshBasicMaterial, Object3D, SphereGeometry } from 'three';
import { createOverlaySphere, pointMarkerRadius, setOverlayBaseColor } from './overlay-style';

export interface PickedPointMarkerSpec {
  /** Stable identity, so a marker that is still wanted is moved rather than rebuilt. */
  key: string;
  /** Position in the parent's coordinates. */
  position: readonly number[];
  color: ColorRepresentation;
}

export const PICKED_POINT_MARKER_NAME = 'picked-point-marker';

/** Radius when the model size is unknown. */
export const DEFAULT_POINT_RADIUS = pointMarkerRadius(null);

/**
 * Radius of a local displacement point marker for a model of the given size.
 * Picked-point markers use the same radius so both read at one scale.
 */
export function localPointMarkerRadius(size: { x: number; y: number; z: number } | null | undefined): number {
  return pointMarkerRadius(size);
}

function isFiniteTriple(value: readonly number[]): boolean {
  return value.length === 3 && value.every((n) => typeof n === 'number' && Number.isFinite(n));
}

/**
 * Small spheres marking points picked in the viewport. Decorative: they never
 * take part in raycasting, so they cannot intercept the clicks that pick.
 */
export class PickedPointMarkers {
  private readonly meshes = new Map<string, Mesh<SphereGeometry, MeshBasicMaterial>>();
  private radius = 0;

  constructor(private readonly parent: () => Object3D) {}

  /**
   * Makes the drawn markers exactly `specs`: markers no longer listed are
   * disposed, listed ones are created or moved. A spec whose position is not
   * three finite numbers draws nothing.
   */
  update(specs: readonly PickedPointMarkerSpec[], radius: number): void {
    const wanted = new Map<string, PickedPointMarkerSpec>();
    for (const spec of specs) {
      if (isFiniteTriple(spec.position)) wanted.set(spec.key, spec);
    }

    const resized = radius !== this.radius;
    this.radius = radius;
    for (const [key, mesh] of [...this.meshes]) {
      if (resized || !wanted.has(key)) this.dispose(key, mesh);
    }
    if (wanted.size === 0) return;

    const root = this.parent();
    for (const spec of wanted.values()) {
      let mesh = this.meshes.get(spec.key);
      if (!mesh) {
        mesh = this.create(radius);
        this.meshes.set(spec.key, mesh);
      }
      if (mesh.parent !== root) root.add(mesh);
      mesh.position.set(spec.position[0], spec.position[1], spec.position[2]);
      setOverlayBaseColor(mesh.material, spec.color);
    }
  }

  clear(): void {
    for (const [key, mesh] of [...this.meshes]) this.dispose(key, mesh);
  }

  get keys(): string[] {
    return [...this.meshes.keys()];
  }

  get(key: string): Mesh<SphereGeometry, MeshBasicMaterial> | undefined {
    return this.meshes.get(key);
  }

  private create(radius: number): Mesh<SphereGeometry, MeshBasicMaterial> {
    const mesh = createOverlaySphere(radius, 0xffffff);
    mesh.name = PICKED_POINT_MARKER_NAME;
    mesh.raycast = (): void => undefined;
    return mesh;
  }

  private dispose(key: string, mesh: Mesh<SphereGeometry, MeshBasicMaterial>): void {
    mesh.removeFromParent();
    mesh.geometry.dispose();
    mesh.material.dispose();
    this.meshes.delete(key);
  }
}
